import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    createKeycloakAccount,
    disableAccount,
    enableAccount,
    getAccountStatus,
    getWhoisByContactId,
    impersonateUser,
    setPrimaryContact,
    syncAccount,
    unlinkAccount,
} from '@/modules/Customer/services'
import { ErrorResponse } from '@/types/shared'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import type { CreateAccountForm } from './types'
import { useCustomerReadOnly } from '../../../CustomerReadOnlyContext'

export function useContactActions(
    contactId: number,
    customerId: number,
    callbacks: {
        onCreateAccountSuccess: () => void
    }
) {
    const { t } = useTranslation()
    const { t: tCustomer } = useTranslation('customer')
    // Chế độ chỉ xem: không gọi whois/account-status (chỉ phục vụ nút ghi đã ẩn)
    const isReadOnly = useCustomerReadOnly()

    // Check if contact can be impersonated based on authentication_method
    const { data: whoisData, isLoading: isWhoisLoading } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.WHOIS, contactId],
            queryFn: () => getWhoisByContactId(contactId),
            enabled: !!contactId && !isReadOnly,
            staleTime: 15 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    // Get account status for disable/enable functionality
    const { data: accountStatus, refetch: refetchAccountStatus } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.ACCOUNT_STATUS, contactId],
                queryFn: () => getAccountStatus(contactId),
                enabled: !!contactId && !isReadOnly,
                staleTime: 30 * 1000,
            },
            { showLoading: false, showError: false }
        )

    const { mutateAsync: handleImpersonate, isPending: isImpersonating } =
        useMutationWithHandler(
            {
                mutationFn: () => impersonateUser(contactId),
            },
            {
                reactQueryKeys: QUERY_KEYS.GENERIC,
                showSuccessToast: false,
                onSuccess: (data) => {
                    if (data?.error) {
                        toast.error(
                            data.error_description || t('impersonation-failed')
                        )
                        return
                    }
                    toast.success(t('impersonation-success'))
                    const result = data?.result
                    if (result?.access_token) {
                        const { access_token, uuid, exp, auth_method } = result

                        // Use route-based auth method: /auth/sso/impersonate or /auth/local/impersonate
                        const method = auth_method === 'local' ? 'local' : 'sso'
                        let impersonateUrl = `${import.meta.env.APP_PARTNER_URL}/auth/${method}/impersonate?uuid=${uuid}&exp=${exp}&jwt=${access_token}`

                        // Carry the DC impersonator identity so the partner console can flag
                        // this session as a DC-driven impersonation (vs a direct customer login).
                        // imp_token is the authoritative, encrypted form the console verifies
                        // server-side via @mgmt; the imp_by_* plaintext is an instant, non-
                        // authoritative display fallback used until (or if) verify is available.
                        if (result.impersonated) {
                            if (result.impersonation_token)
                                impersonateUrl += `&imp_token=${encodeURIComponent(result.impersonation_token)}`
                            if (result.impersonator_id != null)
                                impersonateUrl += `&imp_by_id=${encodeURIComponent(result.impersonator_id)}`
                            if (result.impersonator_name)
                                impersonateUrl += `&imp_by_name=${encodeURIComponent(result.impersonator_name)}`
                            if (result.impersonator_email)
                                impersonateUrl += `&imp_by_email=${encodeURIComponent(result.impersonator_email)}`
                        }

                        window.open(impersonateUrl, '_blank')?.focus()
                    }
                },
                onError: (error: AxiosError<ErrorResponse>) => {
                    toast.error(
                        error?.response?.data?.error_description ||
                            t('impersonation-failed')
                    )
                },
            }
        )

    // Disable account mutation
    const { mutateAsync: handleDisable, isPending: isDisabling } =
        useMutationWithHandler(
            {
                mutationFn: () => disableAccount(contactId),
            },
            {
                reactQueryKeys: [QUERY_KEYS.ACCOUNT_STATUS, QUERY_KEYS.WHOIS],
                successMessage: tCustomer('contact.account-disabled'),
                errorMessage: tCustomer('contact.failed-to-disable-account'),
                onSuccess: () => refetchAccountStatus(),
            }
        )

    // Enable account mutation
    const { mutateAsync: handleEnable, isPending: isEnabling } =
        useMutationWithHandler(
            {
                mutationFn: () => enableAccount(contactId),
            },
            {
                reactQueryKeys: [QUERY_KEYS.ACCOUNT_STATUS, QUERY_KEYS.WHOIS],
                successMessage: tCustomer('contact.account-enabled'),
                errorMessage: tCustomer('contact.failed-to-enable-account'),
                onSuccess: () => refetchAccountStatus(),
            }
        )

    // Sync account mutation
    const { mutateAsync: handleSync, isPending: isSyncing } =
        useMutationWithHandler(
            {
                mutationFn: () => syncAccount(contactId),
            },
            {
                reactQueryKeys: [QUERY_KEYS.ACCOUNT_STATUS, QUERY_KEYS.WHOIS],
                successMessage: tCustomer('contact.account-synced'),
                errorMessage: tCustomer('contact.failed-to-sync-account'),
                onSuccess: () => refetchAccountStatus(),
            }
        )

    // Unlink account mutation
    const { mutateAsync: handleUnlink, isPending: isUnlinking } =
        useMutationWithHandler(
            {
                mutationFn: () => unlinkAccount(contactId),
            },
            {
                reactQueryKeys: [QUERY_KEYS.ACCOUNT_STATUS, QUERY_KEYS.WHOIS],
                successMessage: tCustomer('contact.account-unlinked'),
                errorMessage: tCustomer('contact.failed-to-unlink-account'),
                onSuccess: () => refetchAccountStatus(),
            }
        )

    // Set primary contact mutation
    const { mutateAsync: handleSetPrimary, isPending: isSettingPrimary } =
        useMutationWithHandler(
            {
                mutationFn: () => setPrimaryContact(customerId, contactId),
            },
            {
                reactQueryKeys: [QUERY_KEYS.CONTACTS],
                successMessage: tCustomer('contact.contact-set-as-primary'),
                errorMessage: tCustomer(
                    'contact.failed-to-set-primary-contact'
                ),
            }
        )

    // Create account mutation
    const { mutateAsync: handleCreateAccount, isPending: isCreatingAccount } =
        useMutationWithHandler(
            {
                mutationFn: (formData: CreateAccountForm) =>
                    createKeycloakAccount({
                        contact_id: contactId,
                        password: formData.password,
                        password_confirmation: formData.password_confirmation,
                        send_welcome_email: formData.send_welcome_email,
                    }),
            },
            {
                reactQueryKeys: [
                    QUERY_KEYS.ACCOUNT_STATUS,
                    QUERY_KEYS.WHOIS,
                    QUERY_KEYS.CONTACTS,
                ],
                successMessage: tCustomer('contact.keycloak-account-created'),
                errorMessage: tCustomer(
                    'contact.failed-to-create-keycloak-account'
                ),
                onSuccess: () => {
                    callbacks.onCreateAccountSuccess()
                    refetchAccountStatus()
                },
            }
        )

    const canImpersonate = !isWhoisLoading && whoisData?.valid
    const hasKeycloakAccount =
        accountStatus?.authentication_method === 'keycloak'
    const hasNoAccount = !accountStatus?.has_account
    const isAccountEnabled = accountStatus?.keycloak_status?.enabled ?? false

    return {
        // Queries
        canImpersonate,
        hasKeycloakAccount,
        hasNoAccount,
        isAccountEnabled,
        // Mutations
        handleImpersonate,
        isImpersonating,
        handleDisable,
        isDisabling,
        handleEnable,
        isEnabling,
        handleSync,
        isSyncing,
        handleUnlink,
        isUnlinking,
        handleSetPrimary,
        isSettingPrimary,
        handleCreateAccount,
        isCreatingAccount,
    }
}
